import {
  useCallback,
  useMemo,
} from 'react';
import { useSelector } from 'react-redux';

import { FormatConfig } from '@ezugi/bootstrap/src/store/reducers/config';
import { currentLimitsSelector, gameIdSelector } from '@ezugi/bootstrap/src/store/selectors';
import { formatSelector } from '@ezugi/bootstrap/src/store/selectors/config';
import { userCurrencySelector } from '@ezugi/bootstrap/src/store/selectors/user';
import { getMinMaxLimits } from '@ezugi/bootstrap/src/util/getMinMaxLimits';
import { AMOUNT_TYPES } from '@ezugi/constants';
import { useOrientationChange } from '@ezugi/hooks/src/useOrientationChange';
import { DeviceInfo, formatAmount } from '@ezugi/utils';

import { formatForPortrait, setSpecificAmountFormatTypes } from '../utils';

const useTableLimits = () => {
  const format = useSelector(formatSelector) as FormatConfig;
  const currency = useSelector(userCurrencySelector);
  const gameId = useSelector(gameIdSelector);
  const limits = useSelector(currentLimitsSelector);

  const orientation = useOrientationChange();
  const isPortraitSpecific = DeviceInfo.isHandheld && orientation === 'portrait';

  const minMaxLimit = getMinMaxLimits({ gameId, limits });

  const setCustomFormatConfig = useCallback(
    (amount: number) => ({
      ...format,
      betFormat: isPortraitSpecific
        ? setSpecificAmountFormatTypes(amount)
        : format.betFormat,
      limitFormat: isPortraitSpecific
        ? setSpecificAmountFormatTypes(amount)
        : format.limitFormat,
    }),
    [ format, isPortraitSpecific ]
  );

  const { minLimit, maxLimit } = useMemo(
    () => ({
      minLimit: formatAmount(
        minMaxLimit.min,
        AMOUNT_TYPES.LIMIT,
        setCustomFormatConfig(limits.Min_Bet),
        currency
      ),
      maxLimit: formatAmount(
        minMaxLimit.max,
        AMOUNT_TYPES.LIMIT,
        setCustomFormatConfig(limits.Max_Bet),
        currency
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ limits.Min_Bet, limits.Max_Bet, currency, setCustomFormatConfig ]
  );

  const tableLimits = useMemo(() => {
    if (!isPortraitSpecific) {
      return `${minLimit} - ${maxLimit}`;
    }
    return `${formatForPortrait(limits.Min_Bet, minLimit)}
     -
     ${formatForPortrait(limits.Max_Bet, maxLimit)}`;
  }, [ isPortraitSpecific, minLimit, maxLimit, limits.Min_Bet, limits.Max_Bet ]);

  return tableLimits;
};

export default useTableLimits;
